import React from 'react';
import { Globe, Map } from 'lucide-react';

/**
 * Компонент отображения карты часовых поясов (упрощенная версия с SVG)
 */
const TimezoneMap = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
        <Map className="w-5 h-5 mr-2 text-indigo-500" />
        Карта часовых поясов
      </h2>

      <div className="relative">
        {/* SVG карта с часовыми поясами */}
        <div className="w-full h-full overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-700 p-2 border border-gray-200 dark:border-gray-600 mb-4">
          <svg 
            viewBox="0 0 1000 500" 
            className="w-full h-auto"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Фон карты */}
            <rect x="0" y="0" width="1000" height="500" fill="#E6EFF8" className="dark:fill-gray-800" />
            
            {/* Контуры континентов (упрощенная версия) */}
            <path d="M180,120 Q250,80 300,100 Q330,110 350,90 Q400,40 500,60 Q600,80 650,60 Q700,50 750,80 Q800,110 820,90 L850,110 L830,200 Q800,230 750,250 Q700,260 650,250 Q600,240 550,250 Q500,260 450,250 Q400,240 350,250 Q300,260 250,240 Q200,220 180,230 L150,200 Z" 
              fill="#AED1E6" className="dark:fill-gray-700" stroke="#87CEEB" strokeWidth="2" />
            
            {/* Представление России */}
            <path d="M650,60 Q680,50 700,60 Q750,80 800,70 Q830,60 850,70 Q870,80 890,70 L900,100 Q870,120 850,110 Q830,100 800,110 Q780,120 750,110 Q730,100 700,110 Q670,120 650,110 L630,90 Z" 
              fill="#C1D6E3" className="dark:fill-gray-600" stroke="#6A9FBF" strokeWidth="3" />
            
            {/* Подсветка часовых поясов России */}
            <g className="timezone-zones">
              {/* UTC+2 (Калининград) */}
              <rect x="630" y="60" width="20" height="50" fill="rgba(255, 99, 71, 0.3)" className="dark:fill-red-900/30" />
              <text x="640" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+2</text>
              
              {/* UTC+3 (Москва) */}
              <rect x="650" y="60" width="30" height="50" fill="rgba(255, 165, 0, 0.3)" className="dark:fill-amber-900/30" />
              <text x="665" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+3</text>
              
              {/* UTC+4 (Самара) */}
              <rect x="680" y="60" width="20" height="50" fill="rgba(255, 215, 0, 0.3)" className="dark:fill-yellow-900/30" />
              <text x="690" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+4</text>
              
              {/* UTC+5 (Екатеринбург) */}
              <rect x="700" y="60" width="25" height="50" fill="rgba(144, 238, 144, 0.3)" className="dark:fill-green-900/30" />
              <text x="712" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+5</text>
              
              {/* UTC+6 (Омск) */}
              <rect x="725" y="60" width="25" height="50" fill="rgba(64, 224, 208, 0.3)" className="dark:fill-emerald-900/30" />
              <text x="737" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+6</text>
              
              {/* UTC+7 (Красноярск) */}
              <rect x="750" y="60" width="25" height="50" fill="rgba(30, 144, 255, 0.3)" className="dark:fill-blue-900/30" />
              <text x="762" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+7</text>
              
              {/* UTC+8 (Иркутск) */}
              <rect x="775" y="60" width="25" height="50" fill="rgba(106, 90, 205, 0.3)" className="dark:fill-indigo-900/30" />
              <text x="787" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+8</text>
              
              {/* UTC+9 (Якутск) */}
              <rect x="800" y="60" width="25" height="50" fill="rgba(147, 112, 219, 0.3)" className="dark:fill-purple-900/30" />
              <text x="812" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+9</text>
              
              {/* UTC+10 (Владивосток) */}
              <rect x="825" y="60" width="25" height="50" fill="rgba(238, 130, 238, 0.3)" className="dark:fill-pink-900/30" />
              <text x="837" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+10</text>
              
              {/* UTC+11 (Магадан) */}
              <rect x="850" y="60" width="20" height="50" fill="rgba(255, 105, 180, 0.3)" className="dark:fill-rose-900/30" />
              <text x="860" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+11</text>
              
              {/* UTC+12 (Камчатка) */}
              <rect x="870" y="60" width="20" height="50" fill="rgba(219, 112, 147, 0.3)" className="dark:fill-fuchsia-900/30" />
              <text x="880" y="85" textAnchor="middle" fontSize="8" fill="#333" className="dark:fill-white">+12</text>
            </g>

            {/* Основные часовые пояса мира (упрощенное представление) */}
            <g className="timezone-lines">
              {/* Линии часовых поясов */}
              {Array.from({ length: 24 }, (_, i) => (
                <line 
                  key={i} 
                  x1={100 + i * 33} 
                  y1="0" 
                  x2={100 + i * 33} 
                  y2="500" 
                  stroke="#999" 
                  strokeWidth="1" 
                  strokeDasharray="5,5"
                  className="dark:stroke-gray-600"
                />
              ))}
              
              {/* Подписи к основным часовым поясам */}
              <text x="150" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC-8</text>
              <text x="250" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC-5</text>
              <text x="350" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC-2</text>
              <text x="450" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC+0</text>
              <text x="550" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC+3</text>
              <text x="650" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC+6</text>
              <text x="750" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC+9</text>
              <text x="850" y="20" textAnchor="middle" fontSize="12" fill="#333" className="dark:fill-white">UTC+12</text>
            </g>

            {/* Обозначения городов */}
            <g className="city-markers">
              <circle cx="445" cy="140" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="445" y="130" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Лондон</text>

              <circle cx="470" cy="135" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="470" y="125" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Париж</text>

              <circle cx="665" cy="120" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="665" y="110" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Москва</text>

              <circle cx="250" cy="150" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="250" y="140" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Нью-Йорк</text>

              <circle cx="175" cy="170" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="175" y="160" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Лос-Анджелес</text>

              <circle cx="830" cy="160" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="830" y="150" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Токио</text>
              
              <circle cx="712" cy="130" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="712" y="120" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Екб</text>
              
              <circle cx="837" cy="110" r="5" fill="#E91E63" className="dark:fill-pink-500" />
              <text x="837" y="100" textAnchor="middle" fontSize="10" fill="#333" className="dark:fill-white">Владивосток</text>
            </g>

            {/* Линия международного изменения даты */}
            <line x1="900" y1="0" x2="900" y2="500" stroke="#FF5722" strokeWidth="3" strokeDasharray="10,5" className="dark:stroke-orange-500" />
            <text x="900" y="480" textAnchor="middle" fontSize="12" fill="#FF5722" className="dark:fill-orange-500" transform="rotate(90, 900, 480)">Линия перемены дат</text>
          </svg>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400 mt-3">
          <h3 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2">Интересные факты о часовых поясах России:</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>С 2014 года в России отменен переход на летнее/зимнее время</li>
            <li>Россия имеет 11 часовых поясов — от UTC+2 (Калининград) до UTC+12 (Камчатка)</li>
            <li>Наибольшая разница по времени в пределах России — 10 часов</li>
            <li>Самый "населенный" часовой пояс России — UTC+3 (московское время), в нем проживает более 60% населения страны</li>
            <li>Большинство регионов Европейской части России используют московское время (UTC+3)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TimezoneMap;